import React, { useState } from "react";
import { Box, Button, SxProps } from "@mui/material";
import { BasicButton, COLORS, DefaultSearchBar, Dialog, LazyItem, Loading, NoResultFound, TYPOGRAPHY_COMMON_STYLES, useSearch } from "components";

interface OptionItem {
    id: number;
    name: string;
}

interface SelectionDialogProps {
    isDialogOpen: boolean;
    onDialogClose: () => void;
    onSingleSelect?: (id: number) => void;
    onMultiSelect?: (ids: number[]) => void;
    options: OptionItem[];
    isLoadingOptions?: boolean;
    multiSelect?: boolean;
    dialogTitle?: string;
    mainTitle?: string;
    submitButtonText?: string;
    cancelButtonText?: string;
    isSubmitting?: boolean;
    onCancel?: () => void;
    searchPlaceholder?: string;
    noResultsMessage?: string;
    selectAllText?: string;
    customOptionIcon?: React.ComponentType<any>;
    showSearchBar?: boolean;
    addNewOptionButtonSx?: SxProps;
    addNewOptionButtonVariant?: "outlined" | "contained";
    addNewOptionButtonText?: string;
    addNewOptionButtonIcon?: React.ReactNode;
    addNewOptionButtonOnClick?: () => void;
}

export const SelectionDialog = ({
    isDialogOpen,
    onDialogClose,
    onSingleSelect,
    onMultiSelect,
    options,
    onCancel,
    isLoadingOptions = false,
    multiSelect = false,
    dialogTitle,
    mainTitle = multiSelect ? "Select Sites" : "Select site",
    submitButtonText = "Apply",
    cancelButtonText = 'Cancel',
    isSubmitting = false,
    searchPlaceholder,
    noResultsMessage = "No sites found",
    selectAllText = "Select All",
    customOptionIcon,
    showSearchBar = true,
    addNewOptionButtonSx,
    addNewOptionButtonVariant,
    addNewOptionButtonText,
    addNewOptionButtonIcon,
    addNewOptionButtonOnClick,
}: SelectionDialogProps) => {
    const {
        search,
        onSearchChange: onSearchChangeBase,
        onSearchClear: onSearchClearBase,
    } = useSearch();
    const [filteredOptions, setFilteredOptions] = useState<OptionItem[]>([]);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    const availableOptions = search ? filteredOptions : options || [];

    const onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        const filteredOptionsByName = availableOptions.filter((option) =>
            `${option.name?.toLowerCase()}`.startsWith(value.toLowerCase()),
        );

        setFilteredOptions(filteredOptionsByName || []);
        onSearchChangeBase(value);
    };

    const onSearchClear = () => {
        onSearchClearBase();
        setFilteredOptions([]);
    };

    const handleOptionToggle = (optionId: number) => {
        if (multiSelect) {
            setSelectedIds(prev =>
                prev.includes(optionId)
                    ? prev.filter(id => id !== optionId)
                    : [...prev, optionId]
            );
        } else {
            setSelectedIds([optionId]);
        }
    };

    const handleSingleSelect = (optionId: number) => {
        onSearchClear();
        onSingleSelect?.(optionId);
        onDialogClose();
    };

    const handleMultiSelectSubmit = () => {
        onMultiSelect?.(selectedIds);
        setSelectedIds([]);
        onSearchClear();
        onDialogClose();
    };

    const handleClose = () => {
        setSelectedIds([]);
        onSearchClear();
        onDialogClose();
    };

    const handleSubmit = () => {
        if (multiSelect) {
            handleMultiSelectSubmit();
        } else if (selectedIds.length > 0) {
            handleSingleSelect(selectedIds[0]);
        }
    };

    return (
        <Dialog isOpen={isDialogOpen} dividers onClose={handleClose} title={dialogTitle ? <div style={{ color: COLORS.Mainblue, fontSize: '12px', fontWeight: '600' }}>{dialogTitle}</div> : <div />}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {mainTitle && <div style={{ color: COLORS.Mainblue, fontSize: '20px', fontWeight: '500' }}>
                    {mainTitle}
                </div>}
                {showSearchBar && <DefaultSearchBar searchValue={search} onSearchChange={onSearchChange} placeholder={searchPlaceholder} />}
                {addNewOptionButtonText && <BasicButton
                    variant={addNewOptionButtonVariant || "outlined"}
                    sx={{ marginTop: '0px', marginRight: '0px', fontWeight: '500', ...addNewOptionButtonSx }}
                    startIcon={addNewOptionButtonIcon}
                    onClick={addNewOptionButtonOnClick || (() => { })}
                >{addNewOptionButtonText}</BasicButton>}
                <Box sx={{
                    width: "488px",
                    height: "264px",
                    flexShrink: 0,
                    borderRadius: "10px",
                    border: "1px solid #A9A9A9",
                    overflow: "auto",
                    padding: "0px 32px",
                }}
                >
                    {isLoadingOptions ? (
                        <Box sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100%",
                            flexDirection: "column",
                            backgroundColor: "transparent",
                        }}>
                            <Loading />
                        </Box>
                    ) : availableOptions.length === 0 ? (
                        <Box sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100%",
                            flexDirection: "column",
                            backgroundColor: "transparent",
                        }}>
                            <NoResultFound heading={noResultsMessage} body="" sx={{ color: "#A9A9A9", backgroundColor: "transparent" }} />
                        </Box>
                    ) : (
                        <>
                            {multiSelect && availableOptions.length > 0 && (
                                <LazyItem key="select-all">
                                    <div>
                                        <div
                                            role="button"
                                            tabIndex={0}
                                            style={{
                                                color: COLORS.Romance,
                                                fontWeight: TYPOGRAPHY_COMMON_STYLES.fontWeightSemiBold,
                                                fontSize: 14,
                                                lineHeight: "18px",
                                                backgroundColor: selectedIds.length === availableOptions.length ? "#F6F8FA" : "white",
                                                display: "flex",
                                                alignItems: "center",
                                                padding: "8px 12px",
                                                margin: "2px 0",
                                                borderRadius: "4px",
                                                cursor: "pointer",
                                            }}
                                            onClick={() => {
                                                if (selectedIds.length === availableOptions.length) {
                                                    setSelectedIds([]);
                                                } else {
                                                    setSelectedIds(availableOptions.map(option => option.id));
                                                }
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    if (selectedIds.length === availableOptions.length) {
                                                        setSelectedIds([]);
                                                    } else {
                                                        setSelectedIds(availableOptions.map(option => option.id));
                                                    }
                                                }
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.length === availableOptions.length}
                                                ref={(inputElement) => {
                                                    if (inputElement) {
                                                        // eslint-disable-next-line no-param-reassign
                                                        inputElement.indeterminate = selectedIds.length > 0 && selectedIds.length < availableOptions.length;
                                                    }
                                                }}
                                                onChange={(e) => {
                                                    e.stopPropagation();
                                                    if (selectedIds.length === availableOptions.length) {
                                                        setSelectedIds([]);
                                                    } else {
                                                        setSelectedIds(availableOptions.map(option => option.id));
                                                    }
                                                }}
                                                style={{
                                                    marginRight: 10,
                                                    marginTop: 0,
                                                    marginBottom: 0,
                                                    marginLeft: 0,
                                                    cursor: "pointer",
                                                    width: "20px",
                                                    height: "20px",
                                                    accentColor: "#054254",
                                                    borderRadius: "4px",
                                                }}
                                            />
                                            <p style={{
                                                color: "black",
                                                fontSize: "12px",
                                                fontWeight: "600",
                                            }}>
                                                {selectAllText}
                                            </p>
                                        </div>
                                        <div
                                            style={{
                                                width: "399px",
                                                height: "1px",
                                                opacity: 0.5,
                                                background: "#8CA0A8",
                                                margin: "0 auto",
                                            }}
                                        />
                                    </div>
                                </LazyItem>
                            )}
                            {availableOptions.map((option, index) => (
                                <LazyItem key={option.id}>
                                    <div>
                                        <div
                                            role="button"
                                            tabIndex={0}
                                            style={{
                                                color: COLORS.Romance,
                                                fontWeight: TYPOGRAPHY_COMMON_STYLES.fontWeightSemiBold,
                                                fontSize: 14,
                                                lineHeight: "18px",
                                                backgroundColor: selectedIds.includes(option.id) ? "#F6F8FA" : "white",
                                                display: "flex",
                                                alignItems: "center",
                                                padding: "8px 12px",
                                                margin: "2px 0",
                                                borderRadius: "4px",
                                                cursor: "pointer",
                                            }}
                                            onClick={() => handleOptionToggle(option.id)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    handleOptionToggle(option.id);
                                                }
                                            }}
                                        >
                                            {customOptionIcon ? (
                                                React.createElement(customOptionIcon, {
                                                    style: {
                                                        marginRight: 10,
                                                        marginTop: 0,
                                                        marginBottom: 0,
                                                        marginLeft: 0,
                                                        width: "20px",
                                                        height: "20px",
                                                        color: "#054254",
                                                    }
                                                })
                                            ) : (
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIds.includes(option.id)}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOptionToggle(option.id);
                                                    }}
                                                    style={{
                                                        marginRight: 10,
                                                        marginTop: 0,
                                                        marginBottom: 0,
                                                        marginLeft: 0,
                                                        cursor: "pointer",
                                                        width: "20px",
                                                        height: "20px",
                                                        accentColor: "#054254",
                                                        borderRadius: "4px",
                                                    }}
                                                />
                                            )}
                                            <p style={{
                                                color: "black",
                                                fontSize: "12px",
                                                fontWeight: "600",
                                            }}>
                                                {option.name}
                                            </p>
                                        </div>
                                        {index < availableOptions.length - 1 && (
                                            <div
                                                style={{
                                                    width: "399px",
                                                    height: "1px",
                                                    opacity: 0.5,
                                                    background: "#8CA0A8",
                                                    margin: "0 auto",
                                                }}
                                            />
                                        )}
                                    </div>
                                </LazyItem>
                            ))}
                        </>
                    )}
                </Box>
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "10px", mt: "20px" }}>
                    <Button onClick={onCancel || handleClose}>
                        {cancelButtonText}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting || selectedIds.length === 0}
                        variant="contained"
                        sx={{
                            backgroundColor: "#054254",
                            "&:hover": { backgroundColor: "#043a47" },
                            "&:disabled": { backgroundColor: "#ccc" }
                        }}
                    >
                        {isSubmitting ? 'Saving...' : submitButtonText}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
};
